import dayjs from "dayjs";
import { ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../redux/store";
import { clearGroupInfo, setGroupInfo } from "../../redux/Reducers/gcGroupReducer";
import { T, TF } from "react-translator-component";
import appServices from "../../services/app-services/appServices";
import { getRole, isGroupLeader, isRegularAppUser, findBlockData } from "../../util/utils";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import EventCalendar from "../../components/GreenCity/EventCalender";
import TableComponent from "../../components/GreenCity/TableComponent";
import LoadingModal from "../../components/LoadingModal";
import DisclosureModal from "../../components/Models/GreenCity/DisclosureModal";
import PIIConfirmation from "../../components/Models/GreenCity/PIIConfirmation";
import PIIViewInfo from "../../components/Models/GreenCity/PIIViewInfo";
import MessageModal from "../../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import MebukuIdUpgradeBanner from "../../components/MebukuIdUpgradeBanner";
import BlockModal from "../../components/BlockModal";
import SubmitButton from "../../components/SubmitButton";
import { Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CurrencyYenSharpIcon from "@mui/icons-material/CurrencyYenSharp";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";
import { colors } from "../../common/colors";
import SafeTextWithLinks from "../../components/SafeTextWithLinks";

const defaultImage = require("../../assets/images/default.png");

function GroupInfoReview(): ReactElement {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { user } = useSelector((state: any) => state.userAuth);
  const { blocks } = useSelector((state: any) => state.blocks);
  const { groupId, invitionlink, groupProps } = location.state || {};
  const groupInfo = useSelector((state: any) => state.gcGroup);

  const [loading, setLoading] = useState<boolean>(false);
  const [groupData, setGroupData] = useState<any>()
  const [eventPosts, setEventPosts] = useState<any[]>([]);
  const [eventDateList, setEventDateList] = useState<any[]>([]);
  const [groupIcon, setGroupIcon] = useState<any>();
  const [groupMainImage, setGroupMainImage] = useState<any>();
  const [openDisclosure, setOpenDisclosure] = useState<boolean>(false);
  const [openPIIConfiramtion, setOpenPIIConfiramtion] = useState<boolean>(false);
  const [viewPII, setViewPII] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false);
  const [openErrorMessageModal, setOpenErrorMessageModal] = useState<boolean>(false);
  const [isMember, setIsMember] = useState<boolean>(true);
  const [member, setMember] = useState<any>({});
  const [isInvited, setInvited] = useState<any>(false);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [showBockModal, setShowBockModal] = useState<boolean>(false);
  const [blockId, setBlockId] = useState<number | null>(null);

  useEffect(() => {
    getMemberGroupInfo()
  }, [])

  const getMemberGroupInfo = async () => {
    let groupIconImg;
    let groupBannerImg;
    if (groupId) {
      let resMemberGroup: any;
      try {
        setLoading(true)
        const res = await appServices.getMembersGroup(groupId)
        resMemberGroup = res.member_group
        setGroupData(res.member_group)
      } catch (error) {
        navigate(-1)
      } finally {
        setLoading(false)
      }

      try {
        if (resMemberGroup.group_icon) {
          const iconRes = await appServices.getGroupIcon(groupId)
          groupIconImg = new File([iconRes], resMemberGroup.group_icon, { type: iconRes.type });
          setGroupIcon(groupIconImg);
        }

        if (resMemberGroup.banner_image) {
          const bannerRes = await appServices.getGroupBanner(groupId)
          groupBannerImg = new File([bannerRes], resMemberGroup.banner_image, { type: bannerRes.type });
          setGroupMainImage(groupBannerImg);
        }
      } catch (error) {
        console.error("Failed to get group image")
      }
    } else {
      if (!groupProps) {
        navigate(-1)
        return
      }

      setGroupIcon(groupProps?.groupImage);
      setGroupMainImage(groupProps?.groupMainImage);

      // 表示に必要な分をバックエンドと合わせてキー追加
      setGroupData({
        ...groupProps,
        name: groupProps?.groupName,
        group_icon: groupProps?.groupImage || null,
        banner_image: groupProps?.groupMainImage || null,
        group_type: groupProps?.groupIsPublic ? "Public" : "Private",
        personal_information_needed: groupProps?.piiRequired,
        description: groupProps?.groupDesc,
        social_links: groupInfo?.groupWebURLs,
        approval_required: groupProps?.groupApprovalRequired,
        is_paid: groupProps?.groupMemberShipFeeAmount ? true : false,
        joining_fee_amount: groupProps?.groupMemberShipFeeAmount ?? 0,
        fee_type: groupProps?.isMonthly ? "monthly" : "yearly",
        fee_amount: groupProps?.groupPaymentField ?? "",
        fee_explanation: groupInfo?.groupPaymentDescription,
        member_group_activities: groupProps?.groupAnnouncements,
      })
    }
  }

  const getEvents = async () => {
    try {
      setLoading(true);
      const response = await appServices.getGroupEvents(groupData.id);
      const datesArray = response.posts
        .flatMap((post: { post_events: any[] }) =>
          post.post_events.map((event) => ({
            title: event.title,
            start: event.start_date,
            end: dayjs(event?.end_date).add(1, "day").format("YYYY-MM-DD"),
          }))
        );

      setEventPosts(response.posts);
      setEventDateList(datesArray);
    } catch (error) {
      // pass
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!groupData?.id) return

    if (!groupData?.edit) checkIsMemeber();
    getEvents();
  }, [groupData]);

  useEffect(() => {
    if (!groupData?.id) return
    const blockData = findBlockData(blocks, groupData.id, "MemberGroup")

    setIsBlocked(blockData ? true : false)
    setBlockId(blockData?.id ?? null)
  }, [groupData, blocks]);

  const checkIsMemeber = async () => {
    if (isGroupLeader(groupData, user)) return

    try {
      const res = await appServices.findMember(user.id, groupData.id);
      const memberGroupUser = res?.member_group_user
      setMember(memberGroupUser);
      setIsMember(memberGroupUser ? true : false);
      setInvited(memberGroupUser?.status === "invited");
    } catch {}
  };

  const setMemberGroupUsersParams = () => {
    if (groupData?.approval_required || invitionlink) {
      return {
        body: {
          status: "pending",
          role: "waiting-approval"
        },
        message: T("group_top_succeeded_in_applying")
      }
    } else if (groupData?.is_paid) {
      return {
        body: {
          status: "unpaid_approved",
          role: "member"
        },
        message: T("group_top_joining_fee_is_necessary")
      }
    } else {
      return {
        body: {
          status: "approved",
          role: "member"
        },
        message: T("group_top_succeeded_in_joining")
      }
    }
  };

  const saveMemberGroupUser = async () => {
    try {
      setLoading(true);
      const params = setMemberGroupUsersParams()
      setMessage(params.message);

      if (member?.id) {
        await appServices.updateMemberGroupUsers(member?.id, params.body);
      } else {
        await appServices.createMemberGroupUsers({
          ...params.body,
          member_group_id: groupData.id,
          user_id: user?.id,
        });
      }

      setOpenMessageModal(true)
      return true
    } catch (error: any) {
      console.error(error)
      setErrorMessage(error?.response?.data?.message ?? [T("api_unknown_error")]);
      setOpenErrorMessageModal(true)
      return false
    } finally {
      await checkIsMemeber();
      setLoading(false);
    }
  }

  const getSelectedEvent = (date: any) => {
    const filteredPosts = eventPosts.filter((post: any) =>
      post.post_events.some((event: any) => {
        return event.start_date === date.start && date.end === dayjs(event.end_date).add(1, "day").format("YYYY-MM-DD");
      })
    );

    navigate("/calenderevent", { state: { event: filteredPosts } });
  };

  const handleDisclosuer = () => {
    if (groupData?.personal_information_needed === true) {
      setOpenPIIConfiramtion(true);
    } else {
      saveMemberGroupUser();
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const member_group_activity_values_attributes = [
        {
          member_group_activity_id: 1,
          activity_value: groupInfo?.groupAnnouncements[0].value?.trim() || null,
        },
        {
          member_group_activity_id: 2,
          activity_value: groupInfo?.groupAnnouncements[1].value?.trim() || null,
        },
        {
          member_group_activity_id: 3,
          activity_value: groupInfo?.groupAnnouncements[2].value?.trim() || null,
        },
        {
          member_group_activity_id: 4,
          activity_value: groupInfo?.groupAnnouncements[3].value?.trim() || null,
        },
        {
          member_group_activity_id: 5,
          activity_value: groupInfo?.groupAnnouncements[4].value?.trim() || null,
        },
      ].filter((item) => item.activity_value);

      const body = {
        user_id: user.id,
        name: groupInfo?.groupName,
        group_icon: groupInfo?.groupImage || null,
        banner_image: groupInfo?.groupMainImage || null,
        group_type: groupInfo?.groupIsPublic ? "Public" : "Private",
        personal_information_needed: groupInfo?.piiRequired,
        description: groupInfo?.groupDesc,
        social_links: groupInfo?.groupWebURLs,
        approval_required: groupInfo?.groupApprovalRequired,
        is_paid: groupInfo?.groupMemberShipFeeAmount ? true : false, // need.
        joining_fee_amount: groupInfo?.groupMemberShipFeeAmount ?? 0,
        fee_type: groupInfo?.isMonthly ? "monthly" : "yearly",
        fee_amount: groupInfo?.groupPaymentField ?? "",
        fee_explanation: groupInfo?.groupPaymentDescription,
        member_group_activity_values_attributes: member_group_activity_values_attributes,
      };

      try {
        const res = groupInfo?.edit ?
          await appServices.editGroup(groupInfo.id, body) :
          await appServices.createGroup(body);

        if (res?.data?.success) {
          dispatch(clearGroupInfo());
          setLoading(false);
          setTimeout(() => {
            navigate("/myfarm");
          }, 200);
        }
        return true
      } catch (error: any) {
        console.error(error)
        setErrorMessage(error?.response?.data?.message)
        setOpenErrorMessageModal(true)
        setLoading(false);
        return false
      }
    } catch (error) {
      setLoading(false);
      return false
    }
  };

  const isExistsSubscription = (groupPaymentField: null | number) => {
    return (groupPaymentField && groupPaymentField > 0) ? true : false
  }

  const isExistsJoinFee = (groupMemberShipFeeAmount: null | number) => {
    return (groupMemberShipFeeAmount && groupMemberShipFeeAmount > 0) ? true : false
  }

  const navigateWithGroupInfo = (path: string, state?: any) => {
    dispatch(setGroupInfo({
      id: groupData.id,
      groupName: groupData.name,
      groupIsPublic: groupData.group_type,
      groupImage: groupIcon,
      groupMainImage: groupMainImage,
      groupDesc: groupData.description, // From state
      groupAnnouncements: groupData.member_group_activities, // From state
      groupWebURLs: groupData.social_links, // You can replace this with the actual value
      groupApprovalRequired: groupData.approval_required,
      piiRequired: groupData.personal_information_needed ? "YES" : "NO",
      groupIsLimited: true,
      groupMemberShipFeeAmount: groupData.joining_fee_amount,
      isMonthly: groupData.fee_type === "monthly", // Static value
      groupPaymentField: groupData.fee_amount,
      groupPaymentDescription: groupData.fee_explanation,
      user: groupData.owner,
      owner: groupData.owner,
      role: "leader",
      memberGroupUsers: groupData.member_group_users,
      posts_count:groupData.posts_count,
      isPaid: groupData.is_paid,
      edit: true,
    }));

    navigate(path, {...state});
  }

  const handleNavigateGroupEdit = () => {
    navigateWithGroupInfo("/creategroupstep1");
  }

  const imageCreateObjectURL = (image: any) => {
    return image instanceof File ? URL.createObjectURL(image) : defaultImage;
  }

  const dispRole = () => {
    let userRole = ""
    if (isGroupLeader(groupData, user)) {
      userRole = "leader"
    } else if (groupData?.member_group_users) {
      userRole = groupData?.member_group_users
        .find((member: any) => member.status === "approved" && member.id === user.id)?.group_role
    }

    if (!userRole) return <></>

    return (
      <>
        {TF(T("role_notice"), getRole(userRole))}
        <CheckCircleOutlineIcon style={{ color: colors.farm_green500 }} />
      </>
    )
  }

  const isPublicGroup = (groupType: string) => {
    return groupType === "Public"
  }

  const numberOfApprovedMember = () => {
    const approvedMember = groupData?.member_group_users?.filter((user: any) => user.status === "approved")
    // memberGroupUsers内にリーダーがいないため、最低でも1人になる
    return approvedMember?.length + 1 || 1
  }

  const handleJoinButtonDisable = () => {
    if (
      !isRegularAppUser(user) ||
      isBlocked ||
      (groupData?.personal_information_needed && !user?.name)
    ) return true
  }

  return (
    <div style={styles.container}>
      <GCBackButton onClick={() => navigate(-1)} />
      <LoadingModal visible={loading} />
      <Box style={styles.topSection}>
        <div style={styles.groupNameBox}>
          <img
            alt=""
            style={styles.groupIcon}
            src={imageCreateObjectURL(groupIcon)}
          />
          <Typography style={styles.groupName}>
            &nbsp;{groupData?.name}
          </Typography>
        </div>

        {isBlocked && (
          <div
            style={styles.blockedBtn}
            onClick={() => setShowBockModal(true)}
          >
            {T("is_blocking")}
          </div>
        )}

        {!groupData?.edit && isGroupLeader(groupData, user) && (
          <Typography onClick={handleNavigateGroupEdit}>
             {T("group_top_edit")}
          </Typography>
        )}
      </Box>

      <Box style={styles.groupContentContainer}>
        <Typography>
          {TF(
            T("group_top_group_type_notice"),
            isPublicGroup(groupData?.group_type) ?
              T("group_top_public") : T("group_top_private")
            )
          }
        </Typography>

        <img
          alt=""
          style={styles.groupBannerImg}
          src={imageCreateObjectURL(groupMainImage)}
        />

        <Box style={styles.roleTextBox}>
          <Typography style={styles.roleText}>
            {dispRole()}
          </Typography>
        </Box>

        <Box>
          <Typography>{groupData?.description}</Typography>
        </Box>

        <Typography style={styles.sectionTitle}>
          <CalendarMonthIcon />
          {T("group_top_event_calendar")}
        </Typography>

        <Box>
          <EventCalendar
            getSelectedEvent={getSelectedEvent}
            dates={eventDateList}
          />
        </Box>

        <Box>
          <Typography style={styles.sectionTitle}>
            <CampaignOutlinedIcon style={styles.megaphoneIcon} />
            &nbsp;{T("group_content")}
          </Typography>

          {groupData?.member_group_activities?.length > 0 &&
            groupData.member_group_activities.map((groupActivity: any, index: number) => (
              // 最大5件まで表示
              index < 5 && groupActivity.value && (
                <Typography key={index}>
                  {`・${groupActivity.value}`}
                </Typography>
              )
            ))
          }

          <Typography style={styles.sectionTitle}>
            &nbsp;{T("group_url")}
          </Typography>
          <div>
            <SafeTextWithLinks text={groupData?.social_links} />
          </div>

          <Typography style={styles.sectionTitle}>
            <PeopleAltOutlinedIcon />
            &nbsp;{T("joining_description")}
          </Typography>
          <Typography>
            {!groupData?.approval_required ?
              isPublicGroup(groupData?.group_type) ?
                T("joining_description_anyone") :
                  T("joining_description_invitation") :
                    T("joining_description_approval")
            }
          </Typography>

          {groupData?.personal_information_needed && (
            <Typography>{T("joining_description_name")}</Typography>
          )}

          <Typography style={styles.sectionTitle}>
            <CurrencyYenSharpIcon style={styles.yenIcon}/>
            &nbsp;{T("fee_description")}
          </Typography>
          {
            isExistsJoinFee(groupData?.joining_fee_amount) ||
            isExistsSubscription(groupData?.fee_amount) ? (
              <>
                {isExistsJoinFee(groupData?.joining_fee_amount) && (
                  <Typography>{T("group_top_fee_amount")}{groupData?.joining_fee_amount}{T("unit_yen")}</Typography>
                )}

                {isExistsSubscription(groupData?.fee_amount) && (
                  <>
                    <Typography>
                      {groupData?.fee_type === "monthly" ? T("group_top_monthly_fee_description") : T("group_top_yearly_fee_description")}
                    </Typography>
                    <Typography>
                      {groupData?.fee_type === "monthly" ? T("group_top_monthly_fee") : T("group_top_yearly_fee")}{groupData?.fee_amount}{T("unit_yen")}
                    </Typography>
                  </>
                )}
              </>
            ) : (
              <Typography>{T("group_top_no_joining_fee")}</Typography>
            )
          }

          <Typography>{groupData?.fee_explanation}</Typography>

          <Typography style={styles.sectionTitle}>
            <FeedOutlinedIcon />
            &nbsp;{T("group_top_detail")}
          </Typography>
          <TableComponent
            user={groupData?.id ? groupData?.owner : user}
            data={[
              { col1: T("group_top_group_name"), col2: groupData?.name },
              {
                col1: T("membership_fee"),
                col2:
                  (
                    isExistsJoinFee(groupData?.joining_fee_amount) ||
                    isExistsSubscription(groupData?.fee_amount)
                  ) ? T("group_top_fee_true") : T("group_top_fee_false"),
              },
              {
                col1: T("number_of_member"),
                col2: numberOfApprovedMember() + T("unit_person"),
              },
              { col1: T("number_of_post"), col2: groupData?.posts_count || 0 },
            ]}
          />
        </Box>
      </Box>

      <Box style={styles.btnContainer}>
        {(!groupData?.id || groupData?.edit) && (
          <SubmitButton
            color="secondary"
            style={styles.submitBtn}
            size={"large"}
            variant="contained"
            endIcon={<ArrowForwardIosIcon />}
            validate={async () => true}
            onSubmit={handleSave}
          >
            <span style={styles.submitBtnText}>{T("finish")}</span>
          </SubmitButton>
        )}
      </Box>

      <Box style={styles.btnContainer}>
        {groupData?.id && !groupData?.edit && isPublicGroup(groupData?.group_type) && (
          <Button
            onClick={() => {
              navigateWithGroupInfo("/viewGroupPost", {
                state: {
                  post_type: "public",
                },
              });
            }}
            style={styles.submitBtn}
            size={"large"}
            variant="contained"
            endIcon={<ArrowForwardIosIcon />}
          >
            <span style={styles.submitBtnText}>{T("see_posts")}</span>
          </Button>
        )}
      </Box>

      <Box
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 100
        }}
      >
        {groupData?.id && !isMember && !isGroupLeader(groupData, user) && (
          <div
            style={{textAlign: "center"}}
          >
            <Button
              disabled={handleJoinButtonDisable()}
              onClick={() => setOpenDisclosure(true)}
              style={{
                ...styles.submitBtn,
                color: "white",
                opacity: handleJoinButtonDisable() ? 0.5 : 1,
                backgroundColor: handleJoinButtonDisable() ? colors.farm_gray200 : colors.farm_blue200
              }}
              size={"large"}
              variant="contained"
              endIcon={<ArrowForwardIosIcon />}
            >
              <span style={styles.submitBtnText}>{T("join_group")}</span>
            </Button>
            {(groupData?.personal_information_needed && !user?.name) &&
              <Typography
                style={styles.joinErrorText}
              >
                {T("required_name_to_join_member_group")}
              </Typography>
            }
          </div>
        )}

        {isInvited && (
          <SubmitButton
            color="secondary"
            style={styles.submitBtn}
            size={"large"}
            variant="contained"
            validate={async () => true}
            onSubmit={saveMemberGroupUser}
          >
            <span style={{ padding: "0 auto" }}>{T("join_group")}</span>
          </SubmitButton>
        )}

        <DisclosureModal
          open={openDisclosure}
          onClose={() => setOpenDisclosure(false)}
          onContinue={() => handleDisclosuer()}
        />

        <PIIConfirmation
          open={openPIIConfiramtion}
          onClose={() => setOpenPIIConfiramtion(false)}
          onContinue={() => {
            setOpenPIIConfiramtion(false);
            setViewPII(true);
          }}
        />

        <PIIViewInfo
          groupName={groupData?.name}
          groupImage={imageCreateObjectURL(groupIcon)}
          open={viewPII}
          onClose={() => setViewPII(false)}
          onContinue={() => saveMemberGroupUser()}
        />

        <MessageModal
          open={openMessageModal}
          message={message}
          buttonText={T("close")}
          showButton={groupData?.approval_required}
          buttonStyles={styles.messageModalButton}
          onClose={() => setOpenMessageModal(false)}
          onContinue={() => setOpenMessageModal(false)}
        />

        <ErrorMessageModal
          open={openErrorMessageModal}
          messages={errorMessage}
          onClose={() => setOpenErrorMessageModal(false)}
        />
      </Box>

      {isBlocked && blockId && (
        <BlockModal
          open={showBockModal}
          currentUserId={user.id}
          blockId={blockId}
          targetId={groupData?.id}
          onContinue={() => setShowBockModal(false)}
          onClose={() => setShowBockModal(false)}
        />
      )}

      <MebukuIdUpgradeBanner />
    </div>
  );
}

export default GroupInfoReview;

const styles = {
  container: { paddingTop: 100 },
  topSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomColor: colors.farm_gray15,
    borderBottomWidth: 1,
    borderBottomStyle: "solid" as const,
  },
  groupNameBox: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  groupIcon: {
    height: 32,
    width: 32,
    borderRadius: 18,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.farm_green500,
  },
  groupName: { fontSize: 14, fontWeight: 600 },
  blockedBtn: {
    width: "100px",
    height: "40px",
    fontSize: 14,
    textAlign: 'center' as 'center',
    lineHeight: "34px",
    border: "3px solid orange",
    borderRadius: "5px",
    backgroundColor: "rgba(252, 199, 198, .5)"
  },
  groupContentContainer: { padding: 16, paddingTop: 4 },
  groupBannerImg: {
    marginTop: 4,
    height: 220,
    width: "100%",
  },
  roleTextBox: {
    display: "flex",
    justifyContent: "flex-end",
    margin: 2
  },
  roleText: { fontSize: 10 },
  sectionTitle: {
    borderBottomColor: colors.farm_green150,
    borderBottomWidth: 2,
    borderBottomStyle: "solid" as const,
    fontSize: 14,
    marginBottom: 6,
    marginTop: 16,
  },
  megaphoneIcon: { transform: "rotate(-30deg)" },
  socialLinks: {
    userSelect: "text" as const,
    fontSize: 16,
    fontFamily: "mplus-1c-regular",
    wordWrap: "break-word" as const,
  },
  yenIcon: {
    borderWidth: 1,
    borderStyle: "solid",
    width: 30,
    height: 30,
    borderRadius: 15,
    padding: 2,
    margin: 2,
  },
  btnContainer: {
    display: "flex",
    justifyContent: "center",
  },
  submitBtn: {
    alignSelf: "center",
    borderRadius: 20,
    backgroundColor: colors.farm_blue200,
    marginTop: 40,
    width: 220,
  },
  submitBtnText: { paddingRight: 30, paddingLeft: 40 },
  messageModalButton: {
    color: colors.farm_blue200,
    border: "2px solid ",
    margin: "0px 30px",
    borderRadius: "10px",
    padding: "10px",
    width: "85%",
    maxWidth: 300,
  },
  joinErrorText: {
    color: colors.farm_red600,
    fontSize: 14,
    marginTop: 8
  }
}